/** @jsx jsx */
import React from 'react';
import { jsx } from '@balance-web/core';
import { Button } from '@balance-web/button';
import type { Form } from '@balance-web/forms';
import { Stack } from '@balance-web/stack';
import { Notice } from '@balance-web/notice';
import { Text } from '@balance-web/text';
import { Divider } from '@balance-web/divider';
import { Heading } from '@balance-web/heading';
import { useTheme } from '@balance-web/theme';
import { Field } from '@balance-web/field';
import { TextInput } from '@balance-web/text-input';

import { SelectField } from '../../generic/SelectField';
import { SingleSignOnButton } from '../signIn/singleSignOnButtons/singleSignOnButton';
import {
  AppleIcon,
  GoogleIcon,
  MicrosoftIcon,
} from '../signIn/singleSignOnButtons/singleSignOnButton/Icons';
import type { ErrorResponseType } from '../../useErrorResponses';
import { PasswordValidityCalculator } from '../../passwordStrength/passwordValidityCalculator';
import { Recaptcha } from '../Recaptcha';
import { Config } from '../../../services/Config';

import type { SingupFormSchema } from './formSchema';

export type SignUpFormProps = {
  form: Form<typeof SingupFormSchema>;
  alreadyRegistered?: boolean;
  submitting?: boolean;
  onSubmit: () => void;
  onSignInWithAppleClick?: () => void;
  onSignInWithMicrosoftClick?: () => void;
  onSigninWithGoogleClick?: () => void;
  onLoginClick: () => void;
  error?: ErrorResponseType;
  email?: string;
  inviteId?: string;
  message?: string;
  forMarketing?: boolean;
};

const countries = [
  { value: 'AU', label: 'Australia' },
  { value: 'NZ', label: 'New Zealand' },
];

export const SignUpForm: React.FC<SignUpFormProps> = (props) => {
  const { spacing } = useTheme();
  const {
    form,
    submitting,
    onSubmit,
    onSignInWithAppleClick,
    onSignInWithMicrosoftClick,
    onSigninWithGoogleClick,
    onLoginClick,
    email,
    inviteId,
    message,
    forMarketing,
  } = props;
  return (
    <Stack gap="xlarge" margin="xxlarge">
      <>
        <Heading align="center" level="6">
          {inviteId && email
            ? 'Complete account details'
            : 'Create a new Reckon account'}
        </Heading>
        {!!message && (
          <Notice size="small" tone="critical">
            <Text size="small">{message}</Text>
          </Notice>
        )}
        <form onSubmit={onSubmit}>
          <Stack gap="large">
            <Field
              invalidMessage={
                (form.fields.firstname.state.touched &&
                  form.fields.firstname.props.invalidMessage) ||
                ''
              }
              label="First name"
            >
              <TextInput
                {...form.fields.firstname.props}
                onBlur={(_event) => {
                  form.fields.firstname.setState({
                    ...form.fields.firstname.state,
                    touched: true,
                  });
                }}
              />
            </Field>
            <Field
              invalidMessage={
                (form.fields.lastname.state.touched &&
                  form.fields.lastname.props.invalidMessage) ||
                ''
              }
              label="Last name"
            >
              <TextInput
                {...form.fields.lastname.props}
                onBlur={(_event) => {
                  form.fields.lastname.setState({
                    ...form.fields.lastname.state,
                    touched: true,
                  });
                }}
              />
            </Field>
            <Field
              invalidMessage={
                (form.fields.email.state.touched &&
                  form.fields.email.props.invalidMessage) ||
                ''
              }
              label="Email address"
            >
              <TextInput
                type="email"
                {...(inviteId && email ? { disabled: true } : {})}
                {...form.fields.email.props}
                onBlur={(_event) => {
                  form.fields.email.setState({
                    ...form.fields.email.state,
                    touched: true,
                  });
                }}
              />
            </Field>

            <SelectField
              label="Home country"
              options={countries}
              {...form.fields.country.props}
disabled={forMarketing}
            />
            <Field
              invalidMessage={
                (form.fields.phonenumber.state.touched &&
                  form.fields.phonenumber.props.invalidMessage) ||
                ''
              }
              label="Contact number"
            >
              <TextInput
                {...form.fields.phonenumber.props}
                onBlur={(_event) => {
                  form.fields.phonenumber.setState({
                    ...form.fields.phonenumber.state,
                    touched: true,
                  });
                }}
              />
            </Field>
            <Field
              description="Password must have a minimum of 8 characters with an uppercase, lower case, numeric and special character included."
              invalidMessage={
                (form.fields.password.state.touched &&
                  form.fields.password.props.invalidMessage) ||
                ''
              }
              label="Password"
            >
              <TextInput
                type="password"
                {...form.fields.password.props}
                onBlur={(_event) => {
                  form.fields.password.setState({
                    ...form.fields.password.state,
                    touched: true,
                  });
                }}
              />
            </Field>

            <PasswordValidityCalculator
              password={form.fields.password.props.value}
            />

            <Field
              invalidMessage={
                (form.fields.confirmPassword.state.touched &&
                  form.fields.confirmPassword.props.invalidMessage) ||
                ''
              }
              label="Confirm password"
            >
              <TextInput
                type="password"
                {...form.fields.confirmPassword.props}
                onBlur={(_event) => {
                  form.fields.confirmPassword.setState({
                    ...form.fields.confirmPassword.state,
                    touched: true,
                  });
                }}
              />
            </Field>

            <Recaptcha
              sitekey={Config.APPLICATION_CAPTCHA_SITE_KEY}
              onTokenChange={(token) => {
                if (form.value.captchaToken !== token) {
                  form.fields.captchaToken.props.onChange(token);
                }
              }}
            />
            <Button
              block
              disabled={form.validity !== 'valid'}
              label="Continue"
              loading={submitting}
              type="submit"
            />
          </Stack>
        </form>
      </>

      <Text align="center">- OR -</Text>
      <Stack gap="small">
        {onSigninWithGoogleClick && (
          <SingleSignOnButton
            icon={<GoogleIcon />}
            label="Continue with Google"
            onClick={onSigninWithGoogleClick}
          />
        )}
        {onSignInWithMicrosoftClick && (
          <SingleSignOnButton
            icon={<MicrosoftIcon />}
            label="Continue with Microsoft"
            onClick={onSignInWithMicrosoftClick}
          />
        )}

        {onSignInWithAppleClick && (
          <SingleSignOnButton
            icon={<AppleIcon />}
            label="Continue with Apple"
            onClick={onSignInWithAppleClick}
          />
        )}
      </Stack>

      {!inviteId && (
        <Stack>
          <Divider />
          <Text align="center">
            Already have Reckon account?
            <Button
              css={{ padding: spacing.small }}
              label="Sign in now"
              variant="text"
              onClick={onLoginClick}
            />
          </Text>
        </Stack>
      )}
    </Stack>
  );
};
